import type { Me } from '@myrewards/contracts';
import type { NavigateOptions } from 'react-router';
import { backFromState } from '../awards/record/present';
import type { LevelOneId } from '../shell';
import { isOpenedFrom, itemFromState, pageNameOf } from './nav';

// React Aria links and menu items navigate through React Router; their `routerOptions` are React
// Router's navigate options (RootLayout passes them on), so a link can carry history state.
declare module 'react-aria-components' {
  interface RouterConfig {
    routerOptions: NavigateOptions;
  }
}

/** Where a page was opened from, as the award record reads it (APP-069, QA-045, APP-082). */
export interface BackHere {
  /** The page's name: "Back to {label}". */
  label: string;
  /** The page as it was, with its filters. */
  href: string;
  /** The Level 1 item that page had current, so the record marks the same one. */
  item?: LevelOneId;
}

/**
 * The history state for opening a page that belongs where it was opened from (the award record),
 * from the page the person is on now (APP-082): its name, its address with its filters and the
 * Level 1 item it has current, so the record's back link returns here and the header marks the
 * same item. From the record itself (a link to another award), the record's own way back goes
 * on, so the next one returns to the same list. Undefined on a page with no name: the record
 * falls back to the person's own list.
 */
export function backHere(
  matches: readonly { handle: unknown }[],
  location: { pathname: string; search: string; state: unknown },
  me: Me,
  current: LevelOneId | null,
): { back: BackHere } | undefined {
  if (isOpenedFrom(matches)) {
    const from = backFromState(location.state);
    const item = itemFromState(location.state, me);
    return from === null
      ? undefined
      : { back: { label: from.label, href: from.href, ...(item !== null && { item }) } };
  }
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const label = pageNameOf(matches[i]?.handle, me);
    if (label === null) continue;
    const href = `${location.pathname}${location.search}`;
    return { back: { label, href, ...(current !== null && { item: current }) } };
  }
  return undefined;
}
